/**
 * ASSBuilder — builds an ASS (Advanced SubStation Alpha) subtitle file
 * from grouped word-level lines, rendering eight viral presets via
 * libass-compatible tags (\K, \t, \fad, \move, \c, \fscx, \fscy, \bord, \shad).
 *
 * Output targets:
 *   – FFmpeg WASM with libass built-in (burn-in via -vf ass=subs.ass)
 *   – External players: Aegisub, VLC, DaVinci Resolve, mpv
 *
 * Usage:
 *   var ass = ASSBuilder.build(lines, {
 *     preset: 'hormozi'|'karaoke'|'bounce'|'shake'|'tiktok'|'classic'|'pop'|'fade',
 *     color: '#FFEB3B', font: 'inter'|'bebas'|'impact'|'system',
 *     size: 'sm'|'md'|'lg'|'xl',
 *     position: 'top'|'center'|'bottom',
 *     stroke: true, shadow: false,
 *     stripEmoji: true,
 *     speakers: false, keepOriginal: false,
 *   });
 */
(function(){
    'use strict';

    /* Font name must match the `full-name` recorded inside the TTF `name` table —
       that is what libass uses to resolve a family against a -fontsdir=. */
    var FONT_MAP = {
        inter:      'Inter',                 // Inter-Bold.ttf
        roboto:     'Roboto',                // Roboto-Bold.ttf
        montserrat: 'Montserrat',            // Montserrat-Bold.ttf
        poppins:    'Poppins',               // Poppins-Bold.ttf
        oswald:     'Oswald',                // Oswald-Bold.ttf — condensed
        bebas:      'Bebas Neue',            // BebasNeue-Regular.ttf
        impact:     'Anton',                 // Anton-Regular.ttf — open-source Impact substitute
        noto:       'Noto Sans',             // NotoSans-Bold.ttf — wide Cyrillic + Latin coverage
        marker:     'Permanent Marker',      // PermanentMarker-Regular.ttf — handwritten
        russo:      'Russo One',             // RussoOne-Regular.ttf — bold display, Latin+Cyrillic
        rubik:      'Rubik',                 // Rubik-Black.ttf — heavy impact, Latin+Cyrillic
        caveat:     'Caveat',                // Caveat-Bold.ttf — handwritten, Latin+Cyrillic
        system:     'Arial',                 // libass default fallback
    };
    /* Families whose shipped TTF carries Cyrillic glyphs — these must NOT be
       swapped for Noto over Cyrillic captions (the swap is only for the
       Latin-only display faces). Inter/Oswald are re-shipped with Cyrillic;
       Russo One / Rubik / Caveat are Cyrillic-capable display fonts added for
       the Cyrillic picker; Noto Sans is the universal fallback. Keep in sync
       with the per-option data-scripts in the auto-captions blade picker. */
    var CYRILLIC_CAPABLE = { 'Noto Sans':1, 'Inter':1, 'Oswald':1, 'Russo One':1, 'Rubik':1, 'Caveat':1 };
    var SIZE_MAP = { sm: 52, md: 72, lg: 92, xl: 112 };

    /* ── Script-aware font fallback ──────────────────────────────────
       The display faces Bebas Neue, Anton, Permanent Marker (and the
       pruned Roboto / Montserrat / Poppins / Arial) are Latin-only —
       they have no Cyrillic / Arabic / CJK glyphs. libass inside
       FFmpeg-WASM has no fontconfig fallback chain, so a Latin-only
       `Fontname` over non-Latin captions burns as tofu boxes (□□□).
       That silently broke every Russian / Arabic / CJK burn whose user
       didn't manually pick a covering font.

       So before we commit a Fontname we look at the caption text: if
       its dominant script can't be rendered by the requested font, we
       substitute the covering face (Noto for the universal fallback;
       Inter/Oswald/Russo One/Rubik/Caveat already carry Cyrillic so
       they are left alone — see CYRILLIC_CAPABLE). Latin/English
       captions are never touched — the user's font choice wins. The
       covering font for CJK Han is disambiguated by locale. */
    var SCRIPT_RX = {
        cyrillic: /[Ѐ-ӿԀ-ԯⷠ-ⷿꙀ-ꚟ]/,
        arabic:   /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-\uFEFF]/,
        hangul:   /[가-힣ᄀ-ᇿ㄰-㆏]/,
        kana:     /[぀-ゟ゠-ヿ]/,
        han:      /[㐀-䶿一-鿿豈-﫿]/,
    };
    /* Returns a Fontname that can render `text`'s dominant non-Latin
       script, or null when the requested font already covers it (Latin
       text, or the matching Noto face). Checked most-specific first so a
       Korean caption resolves to KR even though Han also matches CJK. */
    function coveringFontFor(text, requestedFont){
        if (!text) return null;
        if (SCRIPT_RX.cyrillic.test(text)) {
            return CYRILLIC_CAPABLE[requestedFont] ? null : 'Noto Sans';
        }
        if (SCRIPT_RX.arabic.test(text)) {
            return requestedFont === 'Noto Sans Arabic' ? null : 'Noto Sans Arabic';
        }
        if (SCRIPT_RX.hangul.test(text)) {
            return requestedFont === 'Noto Sans KR' ? null : 'Noto Sans KR';
        }
        if (SCRIPT_RX.kana.test(text)) {
            return requestedFont === 'Noto Sans JP' ? null : 'Noto Sans JP';
        }
        if (SCRIPT_RX.han.test(text)) {
            var loc = (typeof window !== 'undefined' && window.__LOCALE) || '';
            var cjk = loc === 'ko' ? 'Noto Sans KR' : (loc === 'ja' ? 'Noto Sans JP' : 'Noto Sans SC');
            return requestedFont === cjk ? null : cjk;
        }
        return null;
    }
    /* Concatenate a representative sample of caption text for script
       detection. Cap the scan so a feature-length transcript stays
       cheap — the dominant script shows up in the first few KB. */
    function sampleText(lines){
        var out = '';
        for (var i = 0; i < lines.length && out.length < 4000; i++){
            var ln = lines[i];
            if (!ln) continue;
            if (ln.text) { out += ln.text + ' '; continue; }
            var ws = ln.words;
            if (!ws) continue;
            for (var j = 0; j < ws.length && out.length < 4000; j++){
                if (ws[j] && ws[j].text) out += ws[j].text + ' ';
            }
        }
        return out;
    }
    var COLOR_DEFAULT = '#FFEB3B';
    /* Palette used when "Detect speakers" is on. Falls back gracefully when
       a tool produces more than three speakers — extra labels round-robin
       through the same palette via getSpeakerColor() below. Default colours
       are picked so they remain readable against both dark and light video
       backdrops and don't collide with the standard active-word yellow. */
    var SPEAKER_COLORS = { A: '#38bdf8', B: '#f472b6', C: '#22c55e', D: '#facc15', E: '#a78bfa', F: '#fb923c' };
    var SPEAKER_PALETTE = ['#38bdf8', '#f472b6', '#22c55e', '#facc15', '#a78bfa', '#fb923c'];

    /* Resolve a speaker label (A/B/C/…) to a hex colour. If a caller has
       written into `__acSpeakerColors.overrides` (rename or palette pick UI),
       that takes precedence so the burned ASS reflects what the user sees
       in the speakers card. */
    function getSpeakerColor(label){
        if (!label) return null;
        var overrides = (window.__acSpeakerColors && window.__acSpeakerColors.overrides) || null;
        if (overrides && overrides[label]) return overrides[label];
        if (SPEAKER_COLORS[label]) return SPEAKER_COLORS[label];
        // Round-robin for labels beyond the named palette (G, H, …)
        var idx = String(label).charCodeAt(0) - 65;
        if (idx < 0) idx = 0;
        return SPEAKER_PALETTE[idx % SPEAKER_PALETTE.length];
    }

    /* Inject a per-speaker primary-colour override into a tag block. We add
       it AFTER existing colour overrides so libass picks the speaker tint
       last — that means speaker tinting wins over preset defaults, which is
       what the user sees in the palette swatch. */
    function applySpeakerTint(baseTags, word, speakersOn) {
        if (!speakersOn || !word) return baseTags;
        var col = word._speakerColor || (word.speaker ? getSpeakerColor(word.speaker) : null);
        if (!col) return baseTags;
        var tint = '\\c' + assColorTag(col);
        if (!baseTags) return '{' + tint + '}';
        if (baseTags.charAt(baseTags.length - 1) === '}') {
            return baseTags.slice(0, -1) + tint + '}';
        }
        return baseTags + '{' + tint + '}';
    }

    function build(lines, opts){
        opts = opts || {};
        var preset   = opts.preset || 'hormozi';
        var activeColor = opts.color || COLOR_DEFAULT;
        var fontName = FONT_MAP[opts.font] || (preset === 'hormozi' || preset === 'pop' ? FONT_MAP.bebas : FONT_MAP.inter);
        // Script-aware fallback: never burn tofu when the chosen Latin
        // face can't render the caption's script (Cyrillic / Arabic / CJK).
        // Records the substitution on the public API so the UI can surface
        // a quiet note + telemetry can count how often it fires.
        api.lastFontSubstitution = null;
        try {
            var requestedFont = fontName;
            var cover = coveringFontFor(sampleText(lines), requestedFont);
            if (cover) {
                fontName = cover;
                api.lastFontSubstitution = { from: requestedFont, to: cover };
            }
        } catch (_) {}
        var fontSize = SIZE_MAP[opts.size] || SIZE_MAP.md;
        var position = opts.position || 'bottom';
        var stroke   = opts.stroke !== false;
        var shadow   = !!opts.shadow;

        var header = headerBlock({
            fontName: fontName,
            fontSize: fontSize,
            position: position,
            stroke: stroke,
            shadow: shadow,
            activeColor: activeColor,
            videoWidth: opts.videoWidth,
            videoHeight: opts.videoHeight,
        });

        var eventsHeader = '[Events]\n'
            + 'Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text\n';
        var events = '';

        var builder = PRESET_BUILDERS[preset] || PRESET_BUILDERS.hormozi;

        for (var i = 0; i < lines.length; i++){
            var ln = lines[i];
            if (!ln.words || !ln.words.length) continue;
            var words = opts.stripEmoji ? ln.words.filter(function(w){ return !w.isEmoji; }) : ln.words.slice();
            // Drop words that sanitize to nothing (e.g. a bare ♪ or 🎵 token) —
            // otherwise they leave stray gaps / zero-width karaoke segments.
            words = words.filter(function(w){ return sanitizeGlyphs(w.text).trim() !== ''; });
            if (!words.length) continue;
            // Rebuild line timing from filtered words to avoid empty lines after emoji strip
            var lineStart = words[0].start, lineEnd = words[words.length - 1].end;
            // Face-aware placement: if the user enabled "Avoid speaker face"
            // AND we have face samples, override the line's alignment when
            // the speaker's face overlaps the default caption zone. The
            // builder reads opts.faceOverride when emitting per-line tags.
            var faceOverride = null;
            if (opts.faceSamples && opts.faceSamples.length && window.__acFace && window.__acFace.placementForLine) {
                faceOverride = window.__acFace.placementForLine(opts.faceSamples, lineStart, opts.faceSamples);
            }
            events += builder({
                start: lineStart,
                end: lineEnd,
                words: words,
                activeColor: activeColor,
                speakers: !!opts.speakers,
                preset: preset,
                faceOverride: faceOverride,
            });
            // Optional second line — original text (when translated + keepOriginal)
            if (opts.keepOriginal && ln.words[0] && ln.words[0]._original){
                events += dialogueLine(lineStart, lineEnd, 'Default', ln.words[0]._original, {
                    position: position,
                    offsetV: fontSize * 1.1,
                    muted: true,
                });
            }
        }

        return header + '\n' + eventsHeader + events;
    }

    /* ═══ Header ═══ */
    /* PlayRes следует за пропорцией КАДРА. libass масштабирует надписи по
       сторонам PlayRes, и с жёстким 1920×1080 на вертикальном 1080×1920
       кегль 72 выходил ~40 px (ширина 1920 → 1080, ×0.56) вместо 128 px, как
       в превью и аппаратном лейне (высота 1080 → 1920, ×1.78). Матрица
       09.09.2026: libass-строка вдвое мельче канвы на том же ролике. Теперь
       PlayResY всегда 1080, PlayResX = 1080 × ширина/высота — масштаб по
       обеим осям одинаков и равен h/1080, как у остальных двух рисовальщиков. */
    var PLAY_RES_Y = 1080;
    function playResFor(cfg){
        var vw = +cfg.videoWidth || 0, vh = +cfg.videoHeight || 0;
        var x = (vw > 0 && vh > 0) ? Math.round(PLAY_RES_Y * vw / vh) : 1920;
        return { x: Math.max(320, x), y: PLAY_RES_Y };
    }
    var lastPlayRes = { x: 1920, y: 1080 };

    /* libass трактует Fontsize как ВЫСОТУ шрифта (ascender+descender), а CSS/канва
       — как em. Поэтому Fontsize 72 у Inter выходит на 1920-высоком кадре
       заглавными в 64 px, тогда как канва и превью при 128 px em дают 93 px:
       один и тот же «M» на ⅓–¾ мельче в файле, чем на экране, и у каждого
       шрифта по-своему (Oswald 1.73, Russo One 1.26). Коэффициенты ЗАМЕРЕНЫ
       09.09.2026 по кадрам: libass (нативный ffmpeg = WASM-сборка, 64 px у
       обоих) против канвы с тем же TTF — tests/scratch/out/ac-bench/
       {libass,canvas}-font-probe.json. Незнакомый шрифт получает медиану. */
    var LIBASS_EM_FACTOR = {
        'Inter': 1.45, 'Oswald': 1.73, 'Bebas Neue': 1.35, 'Anton': 1.79, 'Permanent Marker': 1.50,
        'Noto Sans': 1.44, 'Russo One': 1.26, 'Rubik': 1.55, 'Caveat': 1.31,
    };
    function libassFontSize(sizePt, fontName){
        var f = LIBASS_EM_FACTOR[fontName] || 1.45;
        return Math.round(sizePt * f);
    }

    function headerBlock(cfg){
        var pr = playResFor(cfg);
        lastPlayRes = pr;
        var playResX = pr.x, playResY = pr.y;
        var marginV = cfg.position === 'top' ? Math.round(playResY * 0.08)
                    : cfg.position === 'center' ? Math.round(playResY * 0.45)
                    : Math.round(playResY * 0.10);
        var alignment = cfg.position === 'top' ? 8 : (cfg.position === 'center' ? 5 : 2); // ASS numpad positions
        var borderStyle = 1; // outline + shadow (not box)
        var outline = cfg.stroke ? 4 : 0;
        var shadowVal = cfg.shadow ? 3 : 0;
        var primary = assColor('#FFFFFF', 0);
        var secondary = assColor(cfg.activeColor, 0);
        var outlineCol = assColor('#000000', 0);
        var backCol = assColor('#000000', 160); // semi-transparent shadow

        return '[Script Info]\n'
            + 'ScriptType: v4.00+\n'
            + 'PlayResX: ' + playResX + '\n'
            + 'PlayResY: ' + playResY + '\n'
            + 'ScaledBorderAndShadow: yes\n'
            + 'WrapStyle: 0\n'
            + '\n[V4+ Styles]\n'
            + 'Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding\n'
            + 'Style: Default,' + cfg.fontName + ',' + libassFontSize(cfg.fontSize, cfg.fontName) + ',' + primary + ',' + secondary + ',' + outlineCol + ',' + backCol + ',-1,0,0,0,100,100,0,0,' + borderStyle + ',' + outline + ',' + shadowVal + ',' + alignment + ',80,80,' + marginV + ',1\n';
    }

    /* ═══ Emphasis helper (prosody-derived) ═══
       Each preset controls its own animation; emphasis is layered on top as
       a static style override. We append the override AFTER the base tags
       so later tags take precedence. For animated colour transitions
       (Hormozi, Bounce) this means an emphasised word "settles" to the
       emphasis colour after the animation completes — which is exactly
       the visual we want. */
    function emphasisTags(word) {
        if (!word || !word.emphasis || word.emphasis === 'normal') return '';
        if (word.emphasis === 'high') {
            // ~15% larger + bright yellow (matches editor + overlay CSS)
            return '\\fscx115\\fscy115\\c' + assColorTag('#FFEB3B');
        }
        if (word.emphasis === 'med') {
            return '\\fscx107\\fscy107';
        }
        return '';
    }
    /* Inject emphasis tags into the END of an existing tag block, or wrap
       text with a fresh tag block if no existing one. The result is a
       single self-contained tag string ready to prepend to the word text. */
    function applyEmph(baseTags, word) {
        var emph = emphasisTags(word);
        if (!emph) return baseTags;
        if (!baseTags) return '{' + emph + '}';
        // baseTags looks like '{...}'. Insert emph just before closing brace.
        if (baseTags.charAt(baseTags.length - 1) === '}') {
            return baseTags.slice(0, -1) + emph + '}';
        }
        return baseTags + '{' + emph + '}';
    }

    /* Prepend an `\an{N}` alignment override to a tag block. Used by
       face-aware placement: when the speaker's face overlaps the default
       caption zone, override per-line alignment to push captions away.
       ASS allows multiple alignment overrides per dialogue — the LAST one
       wins, so we inject at the END of the tag block (not the start). */
    function applyFaceOverride(baseTags, faceOverride) {
        if (!faceOverride || !faceOverride.an) return baseTags;
        var alignTag = '\\an' + faceOverride.an;
        if (!baseTags) return '{' + alignTag + '}';
        if (baseTags.charAt(baseTags.length - 1) === '}') {
            return baseTags.slice(0, -1) + alignTag + '}';
        }
        return baseTags + '{' + alignTag + '}';
    }


    /* ═══ Line-with-active-word rendering ═══════════════════════════════════
       До 09.09.2026 шесть «пословных» пресетов (hormozi / bounce / shake / pop /
       mrbeast / gradient) писали ОДИН Dialogue на слово — в прожжённом видео
       на экране стояло одно слово, тогда как превью в плеере (и витрина «Что
       получится», и настройка «Слов в строке») показывали строку с подсветкой
       активного слова. Проверено на проде кадрами: «КАРПЕНДО» → «ПРИНЯТЬ» →
       «ОДНАКО» по одному. Это и есть тот стиль, который «как в CapCut», и
       именно его превью обещало.

       Теперь на каждое слово k — один Dialogue с ЦЕЛОЙ строкой, где слово k
       несёт теги пресета, прошлые слова притушены (как .ac-word.past в превью),
       будущие — базовым цветом. Окно слова тянется до старта следующего, чтобы
       строка не мигала в паузах между словами.

       ⚠️ Внутристрочный \t в libass действует на текст ПОСЛЕ блока до следующего
       переопределения тех же параметров — поэтому после активного слова стоит
       сброс. Масштаб пульса — ТОЛЬКО \fscy: горизонтальный масштаб меняет ширину
       прогона и дёргает соседние слова (в превью transform не перекладывает
       строку, в ASS перекладывает). */
    function lineWithActive(ctx, cfg){
        var words = ctx.words, n = words.length;
        if (!n) return '';
        var out = '';
        var upper = !!cfg.upper;
        var lineTags = cfg.lineTags || '';           // общие для всей строки (обводка, бокс…)
        var baseColor = cfg.baseColor || '#FFFFFF';
        var pastAlpha = cfg.pastAlpha == null ? '38' : cfg.pastAlpha;   // &H38& ≈ 78 % непрозрачности
        for (var k = 0; k < n; k++){
            var wk = words[k];
            var ws = (k === 0) ? Math.min(toCS(ctx.start), toCS(wk.start)) : toCS(wk.start);
            var we = (k < n - 1) ? toCS(words[k + 1].start) : Math.max(toCS(ctx.end), toCS(wk.end));
            if (we <= ws) we = ws + 1;
            var durCs = Math.max(1, toCS(wk.end) - toCS(wk.start));
            var txt = '';
            for (var j = 0; j < n; j++){
                var w = words[j];
                var text = escapeAss(upper ? (w.text || '').toUpperCase() : (w.text || ''));
                var tags;
                if (j === k) {
                    tags = '{\\1a&H00&\\c' + assColorTag(cfg.activeColor || ctx.activeColor) + cfg.active(w, durCs) + '}';
                    tags = applyEmph(tags, w);
                    tags = applySpeakerTint(tags, w, ctx.speakers);
                } else {
                    tags = '{\\fscx100\\fscy100\\frz0\\c' + assColorTag(baseColor) + '\\1a&H' + (j < k ? pastAlpha : '00') + '&}';
                    tags = applySpeakerTint(tags, w, ctx.speakers);
                }
                if (j === 0) {
                    // Общие теги строки и \an — один раз, в первом блоке.
                    tags = lineTags ? tags.slice(0, -1) + lineTags + '}' : tags;
                    tags = applyFaceOverride(tags, ctx.faceOverride);
                }
                txt += tags + text + (j < n - 1 ? ' ' : '');
            }
            out += dialogue(ws, we, txt, 'Default');
        }
        return out;
    }

    /* ═══ Preset builders ═══ */
    var PRESET_BUILDERS = {

        /* 1) Hormozi — UPPERCASE, active word pulses to yellow */
        /* 1) Hormozi — UPPERCASE line, active word pulses (vertical) to yellow */
        hormozi: function(ctx){
            return lineWithActive(ctx, {
                upper: true,
                active: function(w, durCs){
                    var up = Math.min(80, durCs * 50);
                    return '\\fscy120\\t(0,' + up + ',\\fscy100)';
                }
            });
        },

        /* 2) Karaoke — one line, sweep via \K. Speaker tint applies to the
              full line (dominant speaker = first word); karaoke is a
              "future-text → past-text" sweep where past-text uses the
              primary colour and future-text uses secondary, so a per-word
              tint would fight \K and read as flickering. */
        karaoke: function(ctx){
            var ws = toCS(ctx.start), we = toCS(ctx.end);
            var txt = '';
            for (var i = 0; i < ctx.words.length; i++){
                var w = ctx.words[i];
                var durCs = Math.max(1, toCS(w.end) - toCS(w.start));
                txt += '{\\K' + durCs + '}' + escapeAss(w.text) + ' ';
            }
            // Assign SecondaryColour as the "future" text, PrimaryColour will be highlighted by \K
            var karaokeTags = '{\\c' + assColorTag('#FFFFFF') + '\\2c' + assColorTag(ctx.activeColor) + '}';
            karaokeTags = applyFaceOverride(karaokeTags, ctx.faceOverride);
            karaokeTags = applySpeakerTint(karaokeTags, ctx.words[0], ctx.speakers);
            return dialogue(ws, we, karaokeTags + txt.trim(), 'Default');
        },

        /* 3) Bounce — vertical hop on active word */
        /* 3) Bounce — vertical hop on the active word, rest of the line stays */
        bounce: function(ctx){
            return lineWithActive(ctx, {
                active: function(){ return '\\fscy140\\t(0,150,\\fscy100)\\t(150,260,\\fscy110)\\t(260,400,\\fscy100)'; }
            });
        },

        /* 4) Shake — small deterministic jitter */
        /* 4) Shake — small deterministic jitter on the active word. Rotation
              (\frz) is about the LINE origin in ASS, so an inline rotated word
              swings away from its neighbours — the jitter is vertical scale. */
        shake: function(ctx){
            return lineWithActive(ctx, {
                active: function(w){
                    var seed = Math.floor(w.start * 1000) % 7;
                    var a = 104 + seed, b = 96 - (seed % 3);
                    return '\\fscy' + a + '\\t(0,60,\\fscy' + b + ')\\t(60,120,\\fscy102)\\t(120,180,\\fscy100)';
                }
            });
        },

        /* 5) TikTok — white text, semi-transparent dark box via outline.
              For line-level presets we tint by the dominant speaker — the
              diarizer already groups by sentence so each line is normally a
              single speaker; if the user later edits and splits a sentence,
              the first word's speaker still wins (matches competitor behaviour). */
        tiktok: function(ctx){
            var ws = toCS(ctx.start), we = toCS(ctx.end);
            var txt = ctx.words.map(function(w){ return escapeAss(w.text); }).join(' ');
            var tags = '{\\c' + assColorTag('#FFFFFF') + '\\bord8\\3c&H000000&\\3a&H40&\\shad0}';
            tags = applyFaceOverride(tags, ctx.faceOverride);
            tags = applySpeakerTint(tags, ctx.words[0], ctx.speakers);
            return dialogue(ws, we, tags + txt, 'Default');
        },

        /* 6) Classic — simple white-on-black, line-level timing */
        classic: function(ctx){
            var ws = toCS(ctx.start), we = toCS(ctx.end);
            var txt = ctx.words.map(function(w){ return escapeAss(w.text); }).join(' ');
            var classicTags = '{\\c' + assColorTag('#FFFFFF') + '\\bord3}';
            classicTags = applyFaceOverride(classicTags, ctx.faceOverride);
            classicTags = applySpeakerTint(classicTags, ctx.words[0], ctx.speakers);
            return dialogue(ws, we, classicTags + txt, 'Default');
        },

        /* 7) Pop — overshoot scale + color flash */
        /* 7) Pop — overshoot scale + colour flash on the active word */
        pop: function(ctx){
            return lineWithActive(ctx, {
                upper: true,
                active: function(){ return '\\fscy160\\t(0,120,\\fscy92)\\t(120,220,\\fscy100)'; }
            });
        },

        /* 8) Fade — line-level with cross-fade in/out */
        fade: function(ctx){
            var ws = toCS(ctx.start), we = toCS(ctx.end);
            var txt = ctx.words.map(function(w){ return escapeAss(w.text); }).join(' ');
            var fadeTags = '{\\fad(160,160)\\c' + assColorTag('#FFFFFF') + '}';
            fadeTags = applyFaceOverride(fadeTags, ctx.faceOverride);
            fadeTags = applySpeakerTint(fadeTags, ctx.words[0], ctx.speakers);
            return dialogue(ws, we, fadeTags + txt, 'Default');
        },

        /* 9) Mr Beast — punchy yellow text on black-box BG, brief scale-pop
              per word. Line-level dialogue uses BorderStyle override (\bord)
              with very thick outline + dark BG to mimic the chunky-box look
              MrBeast / Hormozi-school videos use. */
        /* 9) Mr Beast — chunky yellow line in a fat black outline; the active
              word flashes white with a quick vertical pop. */
        mrbeast: function(ctx){
            return lineWithActive(ctx, {
                upper: true,
                baseColor: '#FFEB3B',
                activeColor: '#FFFFFF',
                pastAlpha: '20',
                lineTags: '\\bord10\\3c&H000000&\\shad0',
                active: function(w, durCs){ return '\\fscy130\\t(0,' + Math.min(60, durCs * 40) + ',\\fscy100)'; }
            });
        },

        /* 10) Slide-up — line slides up from below the baseline. Uses \move
               to translate the line vertically over 200cs, with \fad on the
               way in. Works at line level (one dialogue per line). */
        slideup: function(ctx){
            var ws = toCS(ctx.start), we = toCS(ctx.end);
            var txt = ctx.words.map(function(w){ return escapeAss(w.text); }).join(' ');
            // Default slides from below to baseline. If face-override
            // pushes us to top (an=8), invert the slide-from to be ABOVE
            // the target so it slides DOWN from off-frame instead.
            var fo = ctx.faceOverride;
            var tags;
            // Координаты — от PlayRes текущей сборки, а не от 1920×1080: на
            // вертикальном кадре PlayResX = 608, и \\pos(960,…) улетал за край.
            var cx = Math.round(lastPlayRes.x / 2), py = lastPlayRes.y;
            if (fo && fo.an === 8) {
                var yTop = Math.round(py * 0.074);
                tags = '{\\fad(150,150)\\an8\\pos(' + cx + ',' + yTop + ')\\move(' + cx + ',' + Math.round(py * 0.0185) + ',' + cx + ',' + yTop + ',0,200)\\c' + assColorTag('#FFFFFF') + '}';
            } else {
                var yBot = Math.round(py * 0.926);
                tags = '{\\fad(150,150)\\an2\\pos(' + cx + ',' + yBot + ')\\move(' + cx + ',' + Math.round(py * 0.981) + ',' + cx + ',' + yBot + ',0,200)\\c' + assColorTag('#FFFFFF') + '}';
            }
            tags = applySpeakerTint(tags, ctx.words[0], ctx.speakers);
            return dialogue(ws, we, tags + txt, 'Default');
        },

        /* 11) Gradient — per-word colour sweep through a 3-stop gradient
               (cyan → yellow → magenta). Uses \1c with \t() to animate the
               primary colour through key timestamps. */
        /* 11) Gradient — the active word sweeps cyan → yellow → magenta over its
               own duration; the rest of the line stays white. */
        gradient: function(ctx){
            return lineWithActive(ctx, {
                activeColor: '#00E5FF',
                active: function(w, durCs){
                    var third = Math.max(1, Math.floor(durCs / 3));
                    return '\\t(0,' + third + ',\\c' + assColorTag('#FFEB3B') + ')\\t(' + third + ',' + (third * 2) + ',\\c' + assColorTag('#FF4081') + ')';
                }
            });
        },
    };

    /* ═══ Dialogue emitters ═══ */
    function dialogue(startCs, endCs, text, style){
        return 'Dialogue: 0,' + assTime(startCs) + ',' + assTime(endCs) + ',' + (style || 'Default') + ',,0,0,0,,' + text + '\n';
    }
    function dialogueLine(startSec, endSec, style, text, opts){
        var tags = '{\\c&HCCCCCC&\\fscx80\\fscy80}';
        return dialogue(toCS(startSec), toCS(endSec), tags + escapeAss(text), style || 'Default');
    }

    /* ═══ Color / time helpers ═══ */
    function assColor(hex, alphaByte){
        // ASS primary color format: &HAABBGGRR (alpha 0 = opaque)
        var h = normaliseHex(hex);
        var a = (alphaByte == null ? 0 : alphaByte).toString(16).toUpperCase().padStart(2, '0');
        var b = h.substring(4, 6).toUpperCase();
        var g = h.substring(2, 4).toUpperCase();
        var r = h.substring(0, 2).toUpperCase();
        return '&H' + a + b + g + r;
    }
    function assColorTag(hex){ return assColor(hex, 0) + '&'; }
    function normaliseHex(hex){
        var s = String(hex || '#FFFFFF').replace(/^#/, '');
        if (s.length === 3) s = s.split('').map(function(c){ return c + c; }).join('');
        if (s.length < 6) s = (s + '000000').substring(0, 6);
        return s.substring(0, 6);
    }
    function toCS(sec){ return Math.max(0, Math.round((sec || 0) * 100)); } // centiseconds
    function assTime(cs){
        if (cs < 0) cs = 0;
        var total = cs;
        var h = Math.floor(total / 360000); total -= h * 360000;
        var m = Math.floor(total / 6000);   total -= m * 6000;
        var s = Math.floor(total / 100);    var c = total - s * 100;
        return h + ':' + pad(m, 2) + ':' + pad(s, 2) + '.' + pad(c, 2);
    }
    function pad(n, w){ var s = String(n); while (s.length < w) s = '0' + s; return s; }

    /* ── Burn-glyph sanitizer ─────────────────────────────────────────
       libass renders EXACTLY the embedded fonts — no system fallback. Our
       caption faces cover letters (Latin/Cyrillic — plus Noto for Arabic/
       CJK via the script fallback), digits and common punctuation, but NOT
       music notes, misc symbols, arrows, dingbats or invisible format
       characters. Whisper emits some of these on songs (♪ music markers,
       narrow no-break spaces from the tokenizer) and they burn as tofu
       boxes — a real user shipped a video with a row of □□□□□ in the
       lyrics (bug report 2026-07-11). Burn-only: the editor, SRT and VTT
       keep the original text (players/browsers render ♪ fine — only the
       burn path is glyph-constrained).
         - space-like exotics (NBSP, en/em/thin/hair spaces, NNBSP, MMSP,
           ideographic space) → plain space;
         - invisible format chars (soft hyphen, ZW*, direction marks, BOM,
           variation selectors) → removed;
         - symbol blocks our fonts don't ship (letterlike, arrows,
           technical, enclosed, box drawing, geometric, misc symbols incl.
           ♪, dingbats, supplemental arrows, emoji planes, PUA, specials
           incl. U+FFFD) → removed;
       then runs of spaces collapse so word spacing stays natural. */
    var GLYPH_SPACE_RX = new RegExp('[\u00A0\u2000-\u200A\u202F\u205F\u3000]', 'g');
    var GLYPH_STRIP_RX = new RegExp('[' +
        '\\u00AD\\u200B-\\u200F\\u202A-\\u202E\\u2060-\\u206F\\uFEFF\\uFE00-\\uFE0F' + // format/invisible
        '\\u2100-\\u2115\\u2117-\\u2121\\u2123-\\u214F\\u2190-\\u21FF\\u2300-\\u23FF\\u2460-\\u24FF\\u2500-\\u25FF' + // letterlike (№ U+2116 + ™ U+2122 KEPT — fonts cover them) / arrows/technical/enclosed/box+geometric
        '\\u2600-\\u27BF\\u2900-\\u297F\\u2B00-\\u2BFF' +                               // misc symbols (incl. ♪) / dingbats / arrows-B / misc-B
        '\\uE000-\\uF8FF\\uFFF0-\\uFFFF' +                                              // PUA / specials (incl. U+FFFD)
        ']|[\\uD83C-\\uD83E][\\uDC00-\\uDFFF]', 'g');                                   // emoji planes (surrogate pairs)
    function sanitizeGlyphs(text){
        return String(text == null ? '' : text)
            .replace(GLYPH_SPACE_RX, ' ')
            .replace(GLYPH_STRIP_RX, '')
            .replace(/ {2,}/g, ' ');
    }

    function escapeAss(text){
        return sanitizeGlyphs(text)
            .replace(/\{/g, '(').replace(/\}/g, ')')  // ASS override blocks use {…}
            .replace(/\n/g, '\\N');
    }

    var api = {
        build: build,
        SPEAKER_COLORS: SPEAKER_COLORS,
        SPEAKER_PALETTE: SPEAKER_PALETTE.slice(),
        getSpeakerColor: getSpeakerColor,
        // Script-aware font fallback (exposed for tests + UI surfacing).
        coveringFontFor: coveringFontFor,
        lastFontSubstitution: null,
        // Burn-glyph sanitizer (exposed for tests).
        sanitizeGlyphs: sanitizeGlyphs,
    };
    if (typeof window !== 'undefined') window.ASSBuilder = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
